import { CRISIS_EFFECTS, PROJECT_RULES, PROJECTS } from '@sd/content';
import { t, type Locale } from '@sd/i18n';
import { projectTotal, sectorOf, type Command, type GameState } from '@sd/sim';
import { clock, escapeHtml, num, seatColor } from './format';

/**
 * The crisis banner over the map (#56, docs/design/ui-style.md): the
 * crisis the player was warned of, what it will do to them, the countdown,
 * and the joint project against it with everyone's share and quick ways
 * to give. Warm, because it is the star speaking.
 */
const GIVE_GOODS = 20;
const GIVE_SCIENCE = 25;

export class CrisisBanner {
  private readonly el: HTMLElement;
  private last = '';
  /** The crisis the player folded the banner for (#156): a new one opens it again. */
  private folded: number | null = null;
  private crisisId: number | null = null;

  constructor(
    parent: HTMLElement,
    private readonly locale: Locale,
    private readonly me: string,
    order: (command: Command) => void,
  ) {
    this.el = document.createElement('section');
    this.el.className = 'sd-panel sd-crisis';
    this.el.setAttribute('aria-live', 'polite');
    this.el.hidden = true;
    parent.append(this.el);
    this.el.addEventListener('click', (event) => {
      if ((event.target as HTMLElement).closest('[data-action="fold"]')) {
        this.folded = this.folded === this.crisisId ? null : this.crisisId;
        this.applyFold();
        return;
      }
      const command = (event.target as HTMLElement).closest<HTMLElement>('[data-command]')?.dataset
        .command;
      if (command) order(JSON.parse(command) as Command);
    });
  }

  /** The banner's height, so the notification stack can start below it. */
  get bottom(): number {
    return this.el.hidden ? 0 : this.el.getBoundingClientRect().bottom;
  }

  update(state: GameState) {
    const view = this.view(state);
    if (!view) {
      this.el.hidden = true;
      this.last = '';
      return;
    }
    this.crisisId = view.crisisId;
    // The frame (and its buttons) is rebuilt only when the crisis, the project or the
    // offers change; the countdown and the shares are written in place, so a click on
    // «Contribute» is never lost to a redraw.
    if (view.layout !== this.last) {
      this.last = view.layout;
      this.el.innerHTML = view.html;
    }
    this.el.hidden = false;
    this.applyFold();
    for (const [name, value] of Object.entries(view.texts)) {
      const node = this.el.querySelector<HTMLElement>(`[data-bind="${name}"]`);
      if (node && node.textContent !== value) node.textContent = value;
    }
    const track = this.el.querySelector<HTMLElement>('[data-bind="track"]');
    if (track && track.innerHTML !== view.track) track.innerHTML = view.track;
  }

  private applyFold() {
    const folded = this.folded !== null && this.folded === this.crisisId;
    this.el.classList.toggle('sd-crisis--folded', folded);
    const button = this.el.querySelector<HTMLButtonElement>('[data-action="fold"]');
    if (button) {
      button.setAttribute('aria-expanded', String(!folded));
      const label = t(this.locale, folded ? 'crisis.unfold' : 'crisis.fold');
      button.title = label;
      button.setAttribute('aria-label', label);
      button.textContent = folded ? '▾' : '▴';
    }
  }

  private view(state: GameState): {
    crisisId: number;
    layout: string;
    html: string;
    texts: Record<string, string>;
    track: string;
  } | null {
    const { locale, me } = this;
    const crisis = state.crises.find((c) => c.warned.includes(me));
    if (!crisis) return null;
    const fx = CRISIS_EFFECTS[crisis.kind];
    const capital = state.system.planets.find((p) => p.homeOf === me)?.hex;
    const where =
      crisis.sector === null
        ? 'crisis.hitsAll'
        : capital && sectorOf(capital) === crisis.sector
          ? 'crisis.hitsYou'
          : 'crisis.hitsSector';
    const effects = t(locale, where, {
      energy: num(locale, fx.blackoutEnergy, 1),
      cycles: fx.blackoutCycles,
      outposts: fx.outpostDownCycles,
      hull: Math.round(fx.hullLoss * 100),
    });
    const texts: Record<string, string> = { timer: clock(crisis.strikeAt - state.tick) };
    const head = `
      <div class="sd-crisis__head">
        <h2 class="sd-crisis__title">${t(locale, `crisis.${crisis.kind}.name`)}</h2>
        <div class="sd-crisis__timer"><span data-bind="timer"></span><small>${t(locale, 'crisis.strikesIn')}</small></div>
        <button type="button" class="sd-btn sd-btn--icon sd-btn--small sd-crisis__fold" data-action="fold" aria-expanded="true">▴</button>
        <div class="sd-crisis__where">${escapeHtml(effects)}</div>
      </div>`;
    const project = state.projects.find((p) => p.crisisId === crisis.id);
    if (!project)
      return { crisisId: crisis.id, layout: `${crisis.id}|${where}`, html: head, texts, track: '' };

    const total = projectTotal(project);
    const scale = (v: number) => Math.min(100, (v / project.full) * 100);
    const parts = Object.entries(project.byPlayer).sort((a, b) => b[1].points - a[1].points);
    const mine = project.byPlayer[me]?.points ?? 0;
    const def = PROJECTS[project.kind];
    const player = state.players.find((p) => p.id === me);
    const gifts: { label: string; command: Command }[] = [];
    if (player) {
      for (const good of def.goods) {
        const quantity = Math.min(GIVE_GOODS, Math.floor(player.goods[good]));
        if (quantity > 0) {
          gifts.push({
            label: t(locale, 'project.giveGood', { quantity, good: t(locale, `resource.${good}`) }),
            command: {
              type: 'contributeProject',
              playerId: me,
              projectId: project.id,
              what: good,
              quantity,
            },
          });
        }
      }
      const science = Math.min(GIVE_SCIENCE, Math.floor(player.planet.science));
      if (science > 0) {
        gifts.push({
          label: t(locale, 'project.giveScience', { quantity: science }),
          command: {
            type: 'contributeProject',
            playerId: me,
            projectId: project.id,
            what: 'science',
            quantity: science,
          },
        });
      }
    }
    texts.progress = t(locale, 'project.progress', {
      total: num(locale, total),
      threshold: num(locale, project.threshold),
      full: num(locale, project.full),
    });
    texts.rule = t(locale, 'project.rule', {
      threshold: num(locale, project.threshold),
      full: num(locale, project.full),
      min: num(locale, def.share * PROJECT_RULES.minCoverShare),
      mine: num(locale, mine),
    });
    const track =
      parts
        .map(([id, c]) => `<span style="--seat:${seatColor(id)};width:${scale(c.points)}%"></span>`)
        .join('') +
      `<span class="sd-project__mark" style="left:${scale(project.threshold)}%"></span>`;
    const html = `${head}
      <div class="sd-project">
        <div class="sd-project__line">
          <b>${t(locale, `project.${project.kind}.name`)}</b>
          <span class="sd-muted" data-bind="progress"></span>
        </div>
        <div class="sd-project__track" data-bind="track"></div>
        <div class="sd-project__foot"><span class="sd-muted" data-bind="rule"></span></div>
        ${
          gifts.length
            ? `<div class="sd-buttons sd-project__gifts"><span class="sd-label">${t(locale, 'project.give')}</span>${gifts
                .map(
                  (g) =>
                    `<button class="sd-btn sd-btn--heat" type="button" data-command="${escapeHtml(JSON.stringify(g.command))}">${escapeHtml(g.label)}</button>`,
                )
                .join('')}</div>`
            : ''
        }
      </div>`;
    return {
      crisisId: crisis.id,
      layout: `${crisis.id}|${where}|${project.id}|${gifts.map((g) => g.label).join(';')}`,
      html,
      texts,
      track,
    };
  }

  dispose() {
    this.el.remove();
  }
}
